import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import "./MyProfile.css";
import logoImage from "../../assets/logo_trucktronix.png";
import iconEmployee from "../../assets/icon_employee.jpg";
import LoadingSpinner from "../../component/LoadingSpinner";

import { serviceFetchAdminDetails } from "../../api/serviceFleetMaster.js";
import { clearAuthSession } from "../../auth/auth";
import { getImageUrl } from "../../utils/helper";

const Profile = () => {
  const navigate = useNavigate();

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfileData = useCallback(async () => {
    try {
      setLoading(true);

      const data = await serviceFetchAdminDetails();

      setInfo(data ?? null);
    } catch (err) {
      console.error("Failed to fetch admin profile:", err);
      toast.error(`Error: ${err?.message ?? "Unknown to load profile"}`);
      setInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchProfileData]);

  const handleLogout = () => {
    clearAuthSession();
    toast.info("Logged out successfully");
    navigate("/login", { replace: true });
  };

  const handleRefresh = () => {
    fetchProfileData();
  };

  const avatarSrc = info?.admin?.imageUrl
    ? getImageUrl(info.admin.imageUrl)
    : iconEmployee;

  return (
    <div className="mp-page">
      {/* Header */}
      <header className="mp-header">
        <button
          type="button"
          className="root-btn-back"
          onClick={() => navigate("/")}
        >
          <i className="bi bi-chevron-left" aria-hidden="true" />
          Back
        </button>

        <div className="mp-header-title">
          <div className="mp-header-label">
            <span>Admin Profile</span>
            <span>Organisation & account details</span>
          </div>

          <img src={logoImage} alt="Trucktronix" className="mp-logo" />
        </div>
      </header>

      {/* Body */}
      <main className="mp-main">
        <section className="mp-card">
          <div className="mp-card-topbar">
            <button
              className="mp-btn-chip"
              onClick={handleRefresh}
              disabled={loading}
              title="Refresh"
            >
              <i className="bi bi-arrow-clockwise" aria-hidden="true" />
              <span>Refresh</span>
            </button>

            <Link
              to="/profile/update"
              className="mp-link"
              aria-label="Edit profile"
            >
              <button className="mp-btn mp-btn-ghost">
                <i className="bi bi-pencil-square" aria-hidden="true" />
                <span style={{ width: "auto" }}>Edit</span>
              </button>
            </Link>
          </div>

          {loading ? (
            <div className="mp-loading">
              <LoadingSpinner loadingText={"Loading profile…"} />
              {/* <div className="mp-loading-text"></div> */}
            </div>
          ) : !info ? (
            <div className="mp-empty">
              <div className="mp-empty-icon">
                <i className="bi bi-person-x" aria-hidden="true" />
              </div>
              <div className="mp-empty-title">No profile data</div>
              <div className="mp-empty-sub">
                We couldn't load your profile right now. Try refreshing.
              </div>
              <button
                type="button"
                className="mp-btn mp-btn-primary"
                onClick={handleRefresh}
              >
                <i className="bi bi-arrow-clockwise" aria-hidden="true" />
                Try again
              </button>
            </div>
          ) : (
            <div className="mp-content">
              <aside className="mp-left">
                <div className="mp-avatar-wrap">
                  <img
                    src={avatarSrc}
                    alt={info?.admin?.name || "Profile"}
                    className="mp-avatar"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = iconEmployee;
                    }}
                  />
                </div>

                <div className="mp-identity">
                  <div className="mp-org">
                    {info?.organization?.organizationName || "Organisation"}
                  </div>
                  <div className="mp-name">{info?.admin?.name || "Admin"}</div>
                </div>

                <div className="mp-actions">
                  <button
                    type="button"
                    className="mp-btn mp-btn-primary"
                    onClick={() => navigate("/password/update")}
                  >
                    <i className="bi bi-key" aria-hidden="true" />
                    <span>Change Password</span>
                  </button>

                  {/* <button
                    type="button"
                    className="mp-btn mp-btn-ghost"
                    onClick={() => navigate('/')}
                  >
                    <i className="bi bi-arrow-left" aria-hidden="true" />
                    Return Home
                  </button> */}

                  <button
                    type="button"
                    className="mp-btn mp-btn-ghost"
                    onClick={handleLogout}
                  >
                    <i className="bi bi-box-arrow-right" aria-hidden="true" />
                    <span>Logout</span>
                  </button>
                </div>
              </aside>

              <section className="mp-right">
                <div className="mp-section-label">
                  <i className="bi bi-shield-lock" aria-hidden="true" />
                  <span>Account Info</span>
                </div>
                <div className="mp-grid">
                  <div className="mp-field">
                    <div className="mp-label">Organization</div>
                    <div className="mp-value">
                      {info?.organization?.organizationCode}
                    </div>
                  </div>

                  <div className="mp-field">
                    <div className="mp-label">Role</div>
                    <div className="mp-value">{info?.admin?.role || "—"}</div>
                  </div>

                  <div className="mp-field mp-field-wide">
                    <div className="mp-label">Address</div>
                    <div className="mp-value mp-wrap">
                      {info?.organization?.address || "—"}
                    </div>
                  </div>

                  <div className="mp-field">
                    <div className="mp-label">Email</div>
                    <div className="mp-value">{info?.admin?.email || "-"}</div>
                  </div>

                  <ProfileField
                    title={"Phone"}
                    value={info?.admin?.phone || "—"}
                  />
                </div>
              </section>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

const ProfileField = ({ title, value }) => {
  return (
    <div className="mp-field">
      <span className="mp-label">{title}</span>
      <span className="mp-value">{value}</span>
    </div>
  );
};

export default Profile;
